import { useCallback } from 'react';
import usePolling from '../../../hooks/usePolling';
import { GetLocationRoutes } from '../../../libs/GraphQL';
import { formatDateForApi } from '../utils';

// Background data heartbeat for the Route Operations tab. Fires every
// `intervalMs` (default 60s) and refreshes the supervisor's view of the
// world without them having to click Apply Filters or "Refresh statuses".
//
// On each tick (when enabled and the tab is visible):
//   - invalidates the cached driver-action statuses → the hook re-fetches
//     and bumps `lastStatusesRefreshedAt` via its existing onLoading flow
//   - re-fetches the staged Nextmv run (via `refreshStagedRun`) and the
//     supervisor actions (via `refreshSupervisorActions`) so another
//     supervisor's reroutes / saved actions converge onto this tab
//   - refetches GetLocationRoutes and pushes the result via
//     `onRoutesFetched(routes)` — UNLESS `hasInflight` is true, in which
//     case `useConfirmReceiptsPoller` is already covering routes at 30s
//     and we'd be duplicating its work. The refresh callbacks above still
//     fire during in-flight ticks; only the routes fetch is suppressed.
//
// The `enabled` gate is the consumer's call (typically: "true when the
// Route Operations tab is mounted"). `usePolling` already skips ticks
// while `document.hidden` so background tabs don't burn cycles.
export default function useRouteOpsHeartbeat({
  enabled,
  hasInflight,
  activeDate,
  onRoutesFetched,
  refreshStatuses,
  refreshStagedRun,
  refreshSupervisorActions,
  intervalMs = 60000,
}) {
  const tick = useCallback(async () => {
    if (!enabled) return;

    if (typeof refreshStatuses === 'function') refreshStatuses();
    if (typeof refreshStagedRun === 'function') refreshStagedRun();
    if (typeof refreshSupervisorActions === 'function') {
      refreshSupervisorActions();
    }

    // `useConfirmReceiptsPoller` already refetches routes every 30s while
    // any reroute is in flight — skipping here keeps us from doubling up.
    if (hasInflight) return;

    try {
      const resp = await GetLocationRoutes({
        stockingDate: formatDateForApi(activeDate),
        calculateTotes: true,
      });
      onRoutesFetched(resp?.routes || []);
    } catch (e) {
      // Heartbeat failures shouldn't bubble — supervisor can still hit
      // Apply Filters manually if data drifts stale. Mirror the same
      // silent-log policy the confirm-poller uses.
      console.error('Route Ops heartbeat fetch failed:', e);
    }
  }, [
    enabled,
    hasInflight,
    activeDate,
    onRoutesFetched,
    refreshStatuses,
    refreshStagedRun,
    refreshSupervisorActions,
  ]);

  // `skipFirstTick: true` — the initial Apply Filters fetch already
  // populates routes / statuses / windows at mount-time. The heartbeat's
  // first tick fires one full `intervalMs` later instead of immediately.
  usePolling(tick, { intervalMs, enabled, skipFirstTick: true });
}
